/* eslint-disable no-irregular-whitespace */
import styled, { css } from 'styled-components';
import { themeable } from 'themes/utils';
import { numberToPx } from 'utils/css';
import { isColumnOptions, getColumnKey } from './utils';
import { ColumnOptions } from './Table.types';
import { Link } from 'ui/Link';
import { checkboxMixin } from 'ui/Select/Select.styled';
import SearchIcon from 'assets/search.svg';
import { animation, animationDuration, animationFunction } from 'tokens';
import { mobileMediaQuery } from 'utils/mediaQuery';

export const Wrapper = styled.div`
    width: 100%;
`;

// Styled контейнер для всего списка
export const Container = styled.div<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    $columns?: Record<string, ColumnOptions<any> | string | undefined>;
    $paddingTop?: boolean;
    $disableScroll?: boolean;
    $maxWidth?: number;
}>`
    min-width: max(
        ${({ $maxWidth }) => ($maxWidth ? `${$maxWidth}px` : '100%')},
        100%
    );

    max-height: 100%;
    ${({ $disableScroll }) =>
        !$disableScroll &&
        css`
            overflow-y: auto;
            overflow-x: hidden;
        `}

    width: max-content;
    flex: 1;
    display: block;
    box-sizing: border-box;
    padding-bottom: 24px;

    ${({ $paddingTop }) =>
        $paddingTop &&
        css`
            padding-top: 24px;
        `}

    ${({ $columns }) =>
        $columns &&
        Object.keys($columns).map((key) => {
            const options = $columns[key];
            if (isColumnOptions(options)) {
                const columnKey = getColumnKey(key);

                return css`
                    & .td-${columnKey}, & .th-${columnKey} {
                        ${options.width &&
                        css`
                            width: ${numberToPx(options.width)};
                            flex: unset;
                        `}
                    }
                `;
            }
        })}

    &::-webkit-scrollbar {
        width: 3px; /* Убираем ширину скроллбара */
    }
`;

export const Body = styled.div`
    width: 100%;
    display: block;
`;

export const RowLink = styled(Link)`
    flex: 1;
`;

// Styled элемент списка
export const Row = styled.div<{
    $hasLink?: boolean;
    $editMode?: boolean;
    $enabled?: boolean;
}>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: ${themeable('secondaryBackground')};
    border-radius: 8px;
    min-width: 200px;
    min-height: 90px;
    box-sizing: border-box;
    flex: 1;

    ${({ $hasLink, $editMode, $enabled }) => css`
        ${!$enabled &&
        $editMode &&
        css`
            opacity: 0.5;
        `}

        ${($hasLink || $editMode) &&
        $enabled &&
        css`
            cursor: pointer;
        `}
    `}
`;

// Styled контейнер для столбцов
export const Cell = styled.div`
    display: flex;
    align-items: center;
    flex-flow: row;
    flex: 1;
    text-align: center; /* Выравнивание текста по центру */
    min-width: 100px;
    text-overflow: ellipsis;
    overflow: hidden;
    padding: 16px;
`;

// Styled контейнер для заголовков
export const Head = styled.div`
    display: block;
    margin: 0 0 20px;
    position: sticky;
    z-index: 1;
    top: 0;
`;

export const BeforeHeaderCell = styled.th`
    width: 100%;
`;

export const HeadRow = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 8px;
    min-width: 200px;
    box-sizing: border-box;
`;

// Styled элемент заголовка колонки
export const HeadingCell = styled.div<{
    $editMode?: boolean;
}>`
    position: relative;
    font-weight: bold;
    color: ${themeable('textColor')};
    padding: 0 8px;
    flex: 1;
    min-width: 100px;
    text-align: center;
    display: flex;
    flex-flow: row;
    align-items: center;
    justify-content: center;

    ${({ $editMode }) =>
        $editMode &&
        css`
            padding-left: 52px;
        `}
`;

export const HeadingCellContainer = styled.div`
    position: relative;

    margin: auto;
    width: max-content;
`;

export const HeadingCellContent = styled.div<{
    $filter?: boolean;
    $activeFilter?: boolean;
}>`
    background: ${themeable('tableHeadingCellBackground')};

    ${({ $activeFilter }) =>
        $activeFilter &&
        css`
            background: ${themeable('primaryColor')} !important;
        `}

    backdrop-filter: blur(36px);
    text-align: center;
    display: block;
    margin: auto;
    width: max-content;
    transition: background ${animation};
    position: relative;
    left: 50%;
    transform: translateX(-50%);
    padding: 6px 12px;
    border-radius: 4px;
    max-width: 100%;

    ${({ $filter }) =>
        $filter &&
        css`
            cursor: pointer;

            &:hover {
                background: ${themeable('tableHeadingCellBackgroundHover')};
            }

            &::after {
                content: url(${SearchIcon});
                width: 12px;
                height: 12px;
                margin-left: 4px;
            }
        `}
`;

export const photoCellMixin = css`
    min-width: unset;
    width: 60px;
`;

export const NoRowsContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 30px 50px 50px;
    font-weight: 500;
    opacity: 0.7;
`;

export const RowContainer = styled.div`
    display: flex;
    flex-flow: row;
    align-items: stretch;
    width: 100%;

    margin-top: 16px;
    gap: 12px;

    & > a {
        width: 100%;
    }
`;

export const Checkbox = styled.div<SActiveProps>`
    height: 16px;
    ${checkboxMixin}
`;

export const CellContent = styled.div`
    flex: 1;
`;

export const CheckboxContainer = styled.div`
    width: 16px;
    height: auto;
    flex: none;
    min-width: unset;
    margin-right: 20px;
    display: inline-block;
`;

export const CellListValue = styled.span`
    white-space: nowrap;
    display: inline-block;

    &:not(:last-child)::after {
        content: ', ';
    }
`;

export const CellListSeparator = styled.span`
    &:last-child {
        display: none;
    }
`;

export const FilterInner = styled.div`
    width: max-content;
    height: max-content;
    max-width: 100%;
`;

export const FilterContainer = styled.div<{ $show?: boolean }>`
    position: absolute;
    left: 0;
    z-index: 50;
    margin-top: 10px;
    transition-property: opacity, visibility;
    transition-duration: ${animationDuration};
    transition-timing-function: ${animationFunction};
    text-align: left;

    ${({ $show }) =>
        !$show &&
        css`
            opacity: 0;
            visibility: hidden;
        `}

    ${mobileMediaQuery(css`
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
    `)}
`;

export const FilterSelectContainer = styled.div`
    width: 344px;
    background: ${themeable('sidebar.background')};
    border-radius: 8px;
    padding: 16px;
`;

export const DeleteFilterContainer = styled.div<SActiveProps>`
    position: absolute;
    z-index: 50;
    margin-left: 8px;
    cursor: pointer;
    background: ${themeable('tableHeadingCellBackgroundHover')};
    transition-property: background, visibility, opacity;
    transition-duration: ${animationDuration};
    transition-timing-function: ${animationFunction};
    border-radius: 4px;
    padding: 4px;
    backdrop-filter: blur(36px);
    display: flex;
    align-items: center;
    justify-content: center;
    top: 0;
    left: 100%;
    height: 28px;
    aspect-ratio: 1;

    &:hover {
        background: rgba(254, 92, 115, 0.5);
    }

    ${({ $active }) =>
        !$active &&
        css`
            visibility: hidden;
            opacity: 0;
        `}
`;
